import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { FormProperty } from '../../models/form.models';
import {
  RecurrenceDefinition,
  RecurrenceType,
} from '../../models/schedule.models';
import { FormsService } from '../../services/forms.service';
import { NomenclatureService } from '../../services/nomenclature.service';
import { UtilsService } from '../../services/utils.service';
import { NomScheduleTypes } from '../../constants';
import {
  buildScheduleEditorProperties,
  ScheduleEditorMode,
  UI_SCHEDULE_TYPE_CRON,
  UI_SCHEDULE_TYPE_INTERVAL,
} from './schedule-form.properties';

/**
 * Standalone recurrence editor for the generic scheduling engine.
 *
 * Attaches its own FormGroup to the host's form under `controlName` (the
 * algorithm-schedule attach pattern), so host validity/dirtiness propagate automatically and
 * the host owns persistence - no NgRx form entity here. The host converts to/from the engine's
 * RecurrenceDefinition JSON contract via toRecurrenceDefinition() /
 * patchFromRecurrenceDefinition().
 *
 * mode 'engine' (default) shows the engine-only controls (cron type, misfire policy,
 * allow overlap); mode 'export' hides them, letting the export-settings panel adopt this
 * component later without behavior change.
 */
@Component({
  selector: 'app-schedule-editor',
  standalone: false,
  templateUrl: './schedule-editor.component.html',
  styleUrl: './schedule-editor.component.scss',
})
export class ScheduleEditorComponent implements OnInit, OnDestroy {
  @Input({ required: true }) parent!: FormGroup;
  @Input() mode: ScheduleEditorMode = 'engine';
  @Input() controlName = 'schedule_editor';
  @Input() initialRecurrence: RecurrenceDefinition | null = null;
  @Input() initialTimezoneId: number | null = null;

  formProperties: FormProperty[] = [];
  formGroup!: FormGroup;

  // Anchor of the interval recurrence being edited ('completion' for the migrated background
  // algorithms) - carried through toRecurrenceDefinition so an edit never flips anchoring.
  private intervalAnchor: 'scheduled' | 'completion' | null = null;

  constructor(
    private readonly translateService: TranslateService,
    private readonly formService: FormsService,
    private readonly nomService: NomenclatureService,
    private readonly utilsService: UtilsService,
  ) {}

  ngOnInit(): void {
    this.formProperties = buildScheduleEditorProperties({
      translate: this.translateService,
      formService: this.formService,
      nomService: this.nomService,
      utilsService: this.utilsService,
      mode: this.mode,
    });

    this.formGroup = this.formService.createFormGroup(this.formProperties);

    if (!this.parent.contains(this.controlName)) {
      this.parent.addControl(this.controlName, this.formGroup);
    }

    if (this.initialRecurrence) {
      this.patchFromRecurrenceDefinition(
        this.initialRecurrence,
        this.initialTimezoneId,
      );
    }
  }

  ngOnDestroy(): void {
    if (this.parent?.contains(this.controlName)) {
      this.parent.removeControl(this.controlName);
    }
  }

  /**
   * The engine RecurrenceDefinition for the current form value - what the schedule API's
   * `recurrence` field expects on create/update.
   */
  toRecurrenceDefinition(): RecurrenceDefinition {
    const value = this.formGroup.getRawValue();
    const scheduleTypeId = Number(value.schedule_type_id);

    let definition: RecurrenceDefinition;

    switch (scheduleTypeId) {
      case NomScheduleTypes.HOURLY:
        definition = {
          type: 'windowed_interval',
          interval_value: Number(value.recurring_period) || 1,
          interval_unit: value.recurring_period_unit ?? 'hours',
          window_start_time: this.toHm(value.recurring_period_start_time),
          window_end_time: this.toHm(value.recurring_period_end_time),
          weekdays: value.schedule_weekdays_to_send ?? [],
        };
        break;
      case NomScheduleTypes.WEEKLY:
        definition = {
          type: 'weekly',
          time_of_day: this.toHm(value.schedule_time_of_day_to_send),
          weekdays: value.schedule_weekdays_to_send ?? [],
        };
        break;
      case NomScheduleTypes.MONTHLY:
        definition = {
          type: 'monthly',
          time_of_day: this.toHm(value.schedule_time_of_day_to_send),
          day_of_month: Number(value.schedule_day_of_month_to_send ?? 1),
        };
        break;
      case UI_SCHEDULE_TYPE_CRON:
        definition = {
          type: 'cron',
          cron_expression: (value.cron_expression ?? '').trim(),
        };
        break;
      case UI_SCHEDULE_TYPE_INTERVAL:
        definition = {
          type: 'interval',
          interval_value: Number(value.recurring_period) || 1,
          interval_unit: value.recurring_period_unit ?? 'minutes',
        };
        if (this.intervalAnchor) {
          definition.anchor = this.intervalAnchor;
        }
        break;
      default:
        definition = {
          type: 'daily',
          time_of_day: this.toHm(value.schedule_time_of_day_to_send),
        };
        break;
    }

    const startDate = this.toUnix(value.schedule_start_date);
    const endDate = this.toUnix(value.schedule_end_date);
    if (startDate) {
      definition.start_date = startDate;
    }
    if (endDate) {
      definition.end_date = endDate;
    }

    return definition;
  }

  /** The selected timezone id (the engine keeps it on the schedule, not in the recurrence). */
  getTimezoneId(): number | null {
    const timezoneId = this.formGroup.get('timezone_id')?.value;

    return timezoneId != null ? Number(timezoneId) : null;
  }

  /** Misfire policy id - engine mode only. */
  getMisfirePolicyId(): number {
    return Number(this.formGroup.get('misfire_policy_id')?.value ?? 1);
  }

  /** Overlap flag - engine mode only. */
  getAllowOverlap(): boolean {
    return !!this.formGroup.get('allow_overlap')?.value;
  }

  /**
   * Populate the form from an engine RecurrenceDefinition (edit mode). Types the toggle
   * cannot express ('once') leave the type untouched and log.
   */
  patchFromRecurrenceDefinition(
    definition: RecurrenceDefinition,
    timezoneId: number | null = null,
  ): void {
    const scheduleTypeId = this.uiTypeFor(definition.type);

    this.intervalAnchor =
      definition.type === 'interval' ? (definition.anchor ?? null) : null;

    if (scheduleTypeId === null) {
      console.warn(
        `ScheduleEditorComponent: recurrence type '${definition.type}' has no editor representation`,
      );
      return;
    }

    this.formGroup.patchValue({
      schedule_type_id: scheduleTypeId,
      timezone_id: timezoneId,
      schedule_time_of_day_to_send: this.utilsService.timeStringToDate(
        definition.time_of_day ?? '00:00',
      ),
      recurring_period: definition.interval_value ?? 1,
      recurring_period_unit: definition.interval_unit ?? 'hours',
      recurring_period_start_time: this.utilsService.timeStringToDate(
        definition.window_start_time ?? '00:00',
      ),
      recurring_period_end_time: this.utilsService.timeStringToDate(
        definition.window_end_time ?? '23:59',
      ),
      cron_expression: definition.cron_expression ?? '',
      schedule_weekdays_to_send: definition.weekdays ?? [],
      schedule_day_of_month_to_send: definition.day_of_month ?? 1,
      schedule_start_date: this.utilsService.unixToDate(definition.start_date),
      schedule_end_date: this.utilsService.unixToDate(definition.end_date),
    });
  }

  private uiTypeFor(type: RecurrenceType): number | null {
    switch (type) {
      case 'daily':
        return NomScheduleTypes.DAILY;
      case 'weekly':
        return NomScheduleTypes.WEEKLY;
      case 'monthly':
        return NomScheduleTypes.MONTHLY;
      case 'windowed_interval':
        return NomScheduleTypes.HOURLY;
      case 'interval':
        return this.mode === 'engine' ? UI_SCHEDULE_TYPE_INTERVAL : null;
      case 'cron':
        return this.mode === 'engine' ? UI_SCHEDULE_TYPE_CRON : null;
      default:
        return null;
    }
  }

  private toHm(date: Date | null | undefined): string {
    const hm = this.utilsService.dateToHm(date);

    if (!hm) {
      return '00:00';
    }

    return `${String(hm.hour).padStart(2, '0')}:${String(hm.minute).padStart(2, '0')}`;
  }

  private toUnix(date: Date | null | undefined): number | null {
    return date instanceof Date ? Math.floor(date.getTime() / 1000) : null;
  }
}
